import { Map, CircleMarker, Icon, IconOptions, polyline } from 'leaflet';
import { marker, icon, circleMarker, geoJSON } from 'leaflet';
import { useEffect, useState } from 'react';

import type {
  EvaluatedStatus,
  DomainGuid
} from '@/features/execute-calculation-set/overmind/calculation';
import { useAppState } from '@/overmind';
import type {
  FeaturesInLayer,
  FeatureWithIDs
} from '@/overmind/effects/adapters/baseadapter';

import { isInitiatingGridElement, RenderedElement } from '../utils/MapElementDesigner';
import { createColorStyles } from '../utils/createColorStyles';
import type {
  ColorStyles,
  StyleMapElementFunction,
  HighlightStates
} from '../utils/styleMapElement';
import {
  styleMapElementForAllCalculations,
  styleMapElementForLvShortCircuit
} from '../utils/styleMapElement';
import type { TacticResultLayers } from './TacticResultLayers';
import { useHighlightedFeatures } from './useHighlightedFeatures';

const useSelectedGridElementId = () => {
  const state = useAppState().calculation.selectedGridElement;
  return state.type === 'SELECTED_GRID_ELEMENT'
    ? state.gridElement.objectId
    : undefined;
};

const useTransformerId = () => {
  const state = useAppState().calculation.calculationTactic;
  return state.current === 'LOADED' && state.value.status === 'Completed'
    ? state.value.calculationSetResult.grid.functional.powerTransformers[0]
        ?.objectId
    : undefined;
};

const colorStyles = createColorStyles({
  selectedElement: '#2ba3ab',
  subnet: '#6db7ae'
});

const useIsGridElementContainer = () => {
  const calculationSetExecution = useAppState().calculation.calculationTactic;
  if(isCalculationTacticResultUnavailable(calculationSetExecution.value)) {
    return () => false;
  }
  const containers = calculationSetExecution.value.calculationSetResult.grid.containment
  return (gridElementId: DomainGuid) => Boolean(containers.find(container => container.containerId === gridElementId))
}

export function UseLvTacticResultLayers(map: Map | null): [TacticResultLayers] {
  const features = useAppState().arcgis.featureGeometries.value;
  const selectedElementId = useSelectedGridElementId();
  const transformerId = useTransformerId();
  const [statuses] = useHighlightedFeatures();
  const isGridElementContainer = useIsGridElementContainer()
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter
  const initiatingGridElement = useAppState().calculation.initiatingGridElement
  const { shouldUseInitiatingObjectHighlighting } = useFeatureToggles();
  const useInitiatingObjectHighlighting = shouldUseInitiatingObjectHighlighting();

  const [layers, setLayers] = useState<TacticResultLayers>({
    layers: [],
    idsInLayers: []
  });

  useEffect(() => {
    const newLayers: TacticResultLayers = {
      layers: [],
      idsInLayers: []
    };
    if (map && features && initiatingGridElement !== undefined) {
      const styleMapElement =
        tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT'
          ? styleMapElementForLvShortCircuit(useInitiatingObjectHighlighting)
          : styleMapElementForAllCalculations(useInitiatingObjectHighlighting);
        
        const featuresWithoutContainers = features.map(
          featureLayer => ({
            ...featureLayer,
            features: featureLayer.features.filter(feature => !isGridElementContainer(feature.gc.globalId))
          })
        );

        const initiatingElementId = initiatingGridElement.globalId;
              const isInitiatingGridElementWithFeatureFlagCheck = 
        (initiatingGridElementId: DomainGuid, gridElementId: DomainGuid) =>
          isInitiatingGridElement(initiatingGridElementId, gridElementId) && useInitiatingObjectHighlighting

        newLayers.layers = mapToStyledLayers(
          featuresWithoutContainers,
          selectedElementId,
          transformerId,
          initiatingElementId,
          statuses,
          styleMapElement,
          createGetTransformerIcon(useInitiatingObjectHighlighting),
          isInitiatingGridElementWithFeatureFlagCheck
        );
        newLayers.idsInLayers = featuresWithoutContainers
          .flatMap(featureLayer => featureLayer.features)
          .map(feature => feature.gc.globalId);
        newLayers.layers.forEach(layer => layer.addTo(map));
    }
    setLayers(newLayers);
    return () => {
      if (map && newLayers.layers) {
        newLayers.layers.forEach(layer => layer.removeFrom(map));
      }
    };
  }, [
    map,
    features,
    statuses,
    selectedElementId,
    transformerId,
    tacticResultBrowsingFilter,
    initiatingGridElement,
    useInitiatingObjectHighlighting
  ]);

  return [layers];
}

function mapToStyledLayers(
  features: FeaturesInLayer[],
  selectedElementId: DomainGuid | undefined,
  startingElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates,
  getColor: StyleMapElementFunction,
  getTransformerIcon: GetTransformerIcon,
  isInitiatingGridElement: (initiatingElementId: DomainGuid, renderedElementId: DomainGuid) => boolean
) {
  return Array.from(features)
    .reverse() // The top layer should be drawn last
    .map(feature =>
      createStyledGeoJsonLayerV2(
        feature,
        selectedElementId,
        startingElementId,
        initiatingElementId,
        statuses,
        colorStyles,
        getColor,
        getTransformerIcon,
        isInitiatingGridElement
      )
    );
}

function createStyledGeoJsonLayer(
  feature: FeaturesInLayer,
  selectedElementId: DomainGuid | undefined,
  startingElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates,
  colorStyles: ColorStyles,
  getColor: StyleMapElementFunction,
  getTransformerIcon: GetTransformerIcon
) {
  return geoJSON(feature, {
    style: featureToStyle => {
      const feature = featureToStyle as FeatureWithIDs;
      const style = getColor(
        RenderedElement.fromFeature(feature),
        selectedElementId,
        initiatingElementId,
        statuses,
        colorStyles
      ) ?? {
        opacity: 0
      }
      
      return style;
    },
    pointToLayer: function (featureToStyle, latlng) {
      const featureId = (featureToStyle as FeatureWithIDs).gc.globalId;
      if (featureId && featureId === startingElementId) {
        const iconToUse = getTransformerIcon(
          featureId,
          selectedElementId,
          initiatingElementId,
          statuses
        );
        if (iconToUse) {
          return marker(latlng, {
            icon: iconToUse.icon
          });
        }
        return undefined as unknown as CircleMarker;
      }
      const resultStyle = getColor(
        RenderedElement.fromFeature(featureToStyle as FeatureWithIDs),
        selectedElementId,
        initiatingElementId,
        statuses,
        colorStyles
      );
      if (resultStyle) {
        return circleMarker(latlng, resultStyle);
      }
      return undefined as unknown as CircleMarker;
    }
  });
}

function createStyledGeoJsonLayerV2(
  feature: FeaturesInLayer,
  selectedElementId: DomainGuid | undefined,
  startingElementId: DomainGuid | undefined,
  initiatingElement: DomainGuid,
  statuses: HighlightStates,
  colorStyles: ColorStyles,
  getColor: StyleMapElementFunction,
  getTransformerIcon: GetTransformerIcon,
  isInitiatingGridElement: (initiatingElementId: DomainGuid, renderedElementId: DomainGuid) => boolean
) {
  const group = geoJSON()
  for (const f of feature.features) {
    const feature = f as FeatureWithIDs;
      const style = getColor(
        RenderedElement.fromFeature(feature),
        selectedElementId,
        initiatingElement,
        statuses,
        colorStyles
      ) ?? {
        opacity: 0
      }
    if(f.geometry.type === 'Point') {
      const latlng = f.geometry.coordinates as [number, number];
      if (f.gc.globalId && f.gc.globalId === startingElementId) {
        const iconToUse = getTransformerIcon(
          f.gc.globalId,
          selectedElementId,
          initiatingElement,
          statuses
        );
        if (iconToUse) {
          const trafoMarker = marker(invert(latlng), {icon: iconToUse.icon });
          group.addLayer(trafoMarker);
        }
      }

      if (style) {
        const marker = circleMarker(invert(latlng), asAny(style));
        group.addLayer(marker);
      }
    } else if (f.geometry.type === 'Polygon' || f.geometry.type === 'LineString') {
      const path = f.geometry.coordinates as [number, number][];
      if(isInitiatingGridElement(initiatingElement, f.gc.globalId)) {
        const lineMarker = polyline(invert(path), asAny(style));
        const borderLineMarker = polyline(invert(path), asAny({...style, color: 'black', weight: style.weight + 5}));
        const initiatingLineMarker = geoJSON()
        initiatingLineMarker.addLayer(borderLineMarker);
        initiatingLineMarker.addLayer(lineMarker);
        group.addLayer(initiatingLineMarker);
      } else {
        const lineMarker = polyline(invert(path), asAny(style));
        group.addLayer(lineMarker);
      }
    }
  }

  return group
}

import substationSvg from '../assets/01_Substation.svg';
import substationBadSvg from '../assets/01_Substation_Bad.svg';
import substationGoodSvg from '../assets/01_Substation_Good.svg';
import substationWarningSvg from '../assets/01_Substation_Warning.svg';
import substationSelectedSvg from '../assets/01_Substation_Selected.svg';
import substationSelectedBadSvg from '../assets/01_Substation_Selected_Bad.svg';
import substationSelectedGoodSvg from '../assets/01_Substation_Selected_Good.svg';
import substationSelectedWarningSvg from '../assets/01_Substation_Selected_Warning.svg';

import initiatingSubstationSvg from '../assets/initiating-grid-element/initiating_01_Substation.svg';
import initiatingSubstationBadSvg from '../assets/initiating-grid-element/initiating_01_Substation_Bad.svg';
import initiatingSubstationGoodSvg from '../assets/initiating-grid-element/initiating_01_Substation_Good.svg';
import initiatingSubstationWarningSvg from '../assets/initiating-grid-element/initiating_01_Substation_Warning.svg';
import initiatingSubstationSelectedSvg from '../assets/initiating-grid-element/initiating_01_Substation_Selected.svg';
import initiatingSubstationSelectedBadSvg from '../assets/initiating-grid-element/initiating_01_Substation_Selected_Bad.svg';
import initiatingSubstationSelectedGoodSvg from '../assets/initiating-grid-element/initiating_01_Substation_Selected_Good.svg';
import initiatingSubstationSelectedWarningSvg from '../assets/initiating-grid-element/initiating_01_Substation_Selected_Warning.svg';
import useFeatureToggles from '@/common/hooks/feature-toggles';
import { isCalculationTacticResultUnavailable } from '@/features/execute-calculation-set/overmind/calculation/actions';

const powerTransformerIcon: SubstationIcon = {
  type: "IRRELEVANT_TO_CALCULATION",
  icon: icon({
    iconUrl: substationSvg,
    iconRetinaUrl: substationSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  })
};
const powerTransformerGoodIcon: SubstationIcon = {
  type: "GOOD",
  icon: icon({
    iconUrl: substationGoodSvg,
    iconRetinaUrl: substationGoodSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  })
};
const powerTransformerBadIcon: SubstationIcon = {
  type: "BAD",
  icon: icon({
    iconUrl: substationBadSvg,
    iconRetinaUrl: substationBadSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  })
};
const powerTransformerWarningIcon: SubstationIcon = {
  type: "WARNING",
  icon: icon({
    iconUrl: substationWarningSvg,
    iconRetinaUrl: substationWarningSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  })
};
const selectedPowerTransformerIcon: SubstationIcon = {
  type: "SELECTED.IRRELEVANT_TO_CALCULATION",
  icon: icon({
    iconUrl: substationSelectedSvg,
    iconRetinaUrl: substationSelectedSvg,
    iconAnchor: [21, 21],
    iconSize: [36, 36]
  })
};
const selectedPowerTransformerGoodIcon: SubstationIcon = {
  type: "SELECTED.GOOD",
  icon: icon({
    iconUrl: substationSelectedGoodSvg,
    iconRetinaUrl: substationSelectedGoodSvg,
    iconAnchor: [21, 21],
    iconSize: [36, 36]
  })
};
const selectedPowerTransformerBadIcon: SubstationIcon = {
  type: "SELECTED.BAD",
  icon: icon({
    iconUrl: substationSelectedBadSvg,
    iconRetinaUrl: substationSelectedBadSvg,
    iconAnchor: [21, 21],
    iconSize: [36, 36]
  })
};
const selectedPowerTransformerWarningIcon: SubstationIcon = {
  type: "SELECTED.WARNING",
  icon: icon({
    iconUrl: substationSelectedWarningSvg,
    iconRetinaUrl: substationSelectedWarningSvg,
    iconAnchor: [21, 21],
    iconSize: [36, 36]
  })
};

function createInitiatingSubstationIcons(): { [key: string]: SubstationIcon} {
  const powerTransformerIcon: SubstationIcon = {
    type: "INITIATING.IRRELEVANT_TO_CALCULATION",
    icon: icon({
      iconUrl: initiatingSubstationSvg,
      iconRetinaUrl: initiatingSubstationSvg,
      iconAnchor: [20, 20],
      iconSize: [30, 30]
    })
  }
  const powerTransformerGoodIcon: SubstationIcon = {
    type: "INITIATING.GOOD",
    icon: icon({
      iconUrl: initiatingSubstationGoodSvg,
      iconRetinaUrl: initiatingSubstationGoodSvg,
      iconAnchor: [20, 20],
      iconSize: [30, 30]
    })
  };
  const powerTransformerWarningIcon: SubstationIcon = {
    type: "INITIATING.WARNING",
    icon: icon({
      iconUrl: initiatingSubstationWarningSvg,
      iconRetinaUrl: initiatingSubstationWarningSvg,
      iconAnchor: [20, 20],
      iconSize: [30, 30]
    })
  }
  const powerTransformerBadIcon: SubstationIcon = {
    type: "INITIATING.BAD",
    icon: icon({
      iconUrl: initiatingSubstationBadSvg,
      iconRetinaUrl: initiatingSubstationBadSvg,
      iconAnchor: [20, 20],
      iconSize: [30, 30]
    })
  }
  const selectedPowerTransformerIcon: SubstationIcon = {
    type: "INITIATING.SELECTED.IRRELEVANT_TO_CALCULATION",
    icon: icon({
      iconUrl: initiatingSubstationSelectedSvg,
      iconRetinaUrl: initiatingSubstationSelectedSvg,
      iconAnchor: [21, 21],
      iconSize: [36, 36]
    })
  };
  const selectedPowerTransformerGoodIcon: SubstationIcon = {
    type: "INITIATING.SELECTED.GOOD",
    icon: icon({
      iconUrl: initiatingSubstationSelectedGoodSvg,
      iconRetinaUrl: initiatingSubstationSelectedGoodSvg,
      iconAnchor: [21, 21],
      iconSize: [36, 36]
    })
  };
  const selectedPowerTransformerBadIcon: SubstationIcon = {
    type: "INITIATING.SELECTED.BAD",
    icon: icon({
      iconUrl: initiatingSubstationSelectedBadSvg,
      iconRetinaUrl: initiatingSubstationSelectedBadSvg,
      iconAnchor: [21, 21],
      iconSize: [36, 36]
    })
  };
  const selectedPowerTransformerWarningIcon: SubstationIcon = {
    type: "INITIATING.SELECTED.WARNING",
    icon: icon({
      iconUrl: initiatingSubstationSelectedWarningSvg,
      iconRetinaUrl: initiatingSubstationSelectedWarningSvg,
      iconAnchor: [21, 21],
      iconSize: [36, 36]
    })
  };
  return {
    powerTransformerIcon,
    powerTransformerGoodIcon,
    powerTransformerBadIcon,
    selectedPowerTransformerIcon,
    selectedPowerTransformerGoodIcon,
    selectedPowerTransformerBadIcon,
    selectedPowerTransformerWarningIcon,
    powerTransformerWarningIcon
  }
}

const initiatingIcons = createInitiatingSubstationIcons();

export const createGetTransformerIcon = (
  shouldUseInitiatingObjectHighlighting: boolean
): GetTransformerIcon => {
  return (
    transformerId: DomainGuid,
    selectedElementId: DomainGuid | undefined,
    initiatingElementId: DomainGuid,
    statuses: HighlightStates
  ) => {
    const isSelected = transformerId === selectedElementId;
    const isInitiating = transformerId === initiatingElementId && shouldUseInitiatingObjectHighlighting;
    const status: EvaluatedStatus | undefined =
      statuses.type === 'SOME_HIGHLIGHTED'
        ? statuses.context.highlighted[transformerId]
        : undefined;
    if (status === 'Good') {
      return isSelected
        ? isInitiating? initiatingIcons.selectedPowerTransformerGoodIcon : selectedPowerTransformerGoodIcon
        : isInitiating? initiatingIcons.powerTransformerGoodIcon : powerTransformerGoodIcon;
    }
    if (status === 'Bad') {
      return isSelected
      ? isInitiating? initiatingIcons.selectedPowerTransformerBadIcon : selectedPowerTransformerBadIcon
      : isInitiating? initiatingIcons.powerTransformerBadIcon : powerTransformerBadIcon;
    }
    if (status === 'Warning') {
      return isSelected
        ? isInitiating? initiatingIcons.selectedPowerTransformerWarningIcon : selectedPowerTransformerWarningIcon
        : isInitiating? initiatingIcons.powerTransformerWarningIcon : powerTransformerWarningIcon;
    }
    return isSelected
            ? isInitiating? initiatingIcons.selectedPowerTransformerIcon : selectedPowerTransformerIcon
            : isInitiating? initiatingIcons.powerTransformerIcon : powerTransformerIcon;
  }
}

export type GetTransformerIcon = typeof _getTransformerIcon;

type SubstationIcon = {
  type: 
    | "INITIATING.SELECTED.GOOD"
    | "INITIATING.SELECTED.BAD"
    | "INITIATING.SELECTED.WARNING"
    | "INITIATING.SELECTED.IRRELEVANT_TO_CALCULATION"
    
    | "INITIATING.GOOD"
    | "INITIATING.BAD"
    | "INITIATING.WARNING"
    | "INITIATING.IRRELEVANT_TO_CALCULATION"

    | "SELECTED.GOOD"
    | "SELECTED.BAD"
    | "SELECTED.WARNING"
    | "SELECTED.IRRELEVANT_TO_CALCULATION"

    | "GOOD"
    | "BAD"
    | "WARNING"
    | "IRRELEVANT_TO_CALCULATION"
  icon: Icon<IconOptions>;
}
function _getTransformerIcon(
  transformerId: DomainGuid,
  selectedElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates
): SubstationIcon {
  const isSelected = transformerId === selectedElementId;
  const isInitiating = transformerId === initiatingElementId;
  const status: EvaluatedStatus | undefined =
    statuses.type === 'SOME_HIGHLIGHTED'
      ? statuses.context.highlighted[transformerId]
      : undefined;
  if (status === 'Good') {
    return isSelected
      ? isInitiating? initiatingIcons.selectedPowerTransformerGoodIcon : selectedPowerTransformerGoodIcon
      : isInitiating? initiatingIcons.powerTransformerGoodIcon : powerTransformerGoodIcon;
  }
  if (status === 'Bad') {
    return isSelected
    ? isInitiating? initiatingIcons.selectedPowerTransformerBadIcon : selectedPowerTransformerBadIcon
    : isInitiating? initiatingIcons.powerTransformerBadIcon : powerTransformerBadIcon;
  }
  if (status === 'Warning') {
    return isSelected
      ? isInitiating? initiatingIcons.selectedPowerTransformerWarningIcon : selectedPowerTransformerWarningIcon
      : isInitiating? initiatingIcons.powerTransformerIcon : powerTransformerIcon;
  }
  return isSelected ? selectedPowerTransformerIcon : powerTransformerIcon;
}
function asAny(style: any): any {
  return style as any;
}

function invert(latlng: [number, number] | [number, number][]): [number, number] | [number, number][] {
  if (Array.isArray(latlng[0])) {
    return (latlng as [number, number][]).map(([lat, lng]) => [lng, lat] as [number, number]);
  }
  return [latlng[1], latlng[0]] as [number, number];
}

